import axios from 'axios';
import { getApiBase } from '@/config/env';

// The single configured axios instance. Every api module goes through here.
// baseURL is resolved per-request so a runtime origin override (Settings) applies immediately,
// and absolute URLs (health endpoints at the origin root) bypass it.
const axiosInstance = axios.create({
  timeout: 60_000, // generous — generation kick-off calls can be slow
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use((config) => {
  const url = config.url || '';
  if (!/^https?:\/\//i.test(url)) {
    config.baseURL = getApiBase();
  }
  return config;
});

// Unwrap to response.data on success; normalize errors to a stable { status, code, message }.
// Errors now travel inside the standard envelope: a human-readable `message` at the top level and
// the machine code at `data.code` (the pre-envelope `error.code`/`error.message` is read as a
// last-resort fallback for resilience).
axiosInstance.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;
    const normalized = {
      status,
      code:
        body?.data?.code ||
        body?.error?.code ||
        (error.code === 'ECONNABORTED' ? 'timeout' : status ? 'error' : 'network_error'),
      message:
        body?.message ||
        body?.data?.message ||
        body?.error?.message ||
        (error.code === 'ECONNABORTED'
          ? 'The request timed out. Please try again.'
          : status === 0
            ? 'Cannot reach the server. Check your connection or the API origin in Settings.'
            : error.message || 'Request failed'),
      isNetwork: !error.response,
    };
    return Promise.reject(normalized);
  },
);

const request = (method, url, { params, data, ...rest } = {}) =>
  axiosInstance.request({ method, url, params, data, ...rest });

const apiClient = {
  get: (url, config = {}) => request('get', url, config),
  post: (url, data, config = {}) => request('post', url, { ...config, data }),
  put: (url, data, config = {}) => request('put', url, { ...config, data }),
  patch: (url, data, config = {}) => request('patch', url, { ...config, data }),
  delete: (url, config = {}) => request('delete', url, config),
  raw: axiosInstance,
};

export default apiClient;
